import { defineStore } from "pinia"
import { Preferences } from "@capacitor/preferences"
import { Device } from "@capacitor/device"
import { Center, OauthConfig, Token, User } from "../user"
import { UserApi } from "../api/userApi"
import { oauthConfig } from "../OauthConfig"
import { AuthAxios } from "../api/AuthAxios"
import { OauthTools } from "../oauth"
// import { Browser } from "@capacitor/browser"
import { AuthGokartsApi } from "../api/AuthGokartsApi"
import { AxiosError } from "axios"
import { useAppSettingsStore } from "./AppSettingsStore"
import log from "../utils/logger"

interface UserManagerState {
    user: User | null,
    token: Token | null,
    oauth_config: OauthConfig,
    requests_authorized: boolean,
    refreshTokenLeadTime: number, // Time in seconds before the token expires to refresh it
    isExternalSession: boolean
}

export const useUserManagerStore = defineStore('user-manager', {
    state: (): UserManagerState => ({
        user: null,
        token: null,
        oauth_config: oauthConfig,
        requests_authorized: false,
        refreshTokenLeadTime: 5 * 60, // in seconds (token expires after 1 hour. Refresh it 5 minutes before) Refresh token is valid for 1 year
        isExternalSession: false
    }),

    getters: {
        displayedCenters(): Center[] {
            const centers = this.user?.centers ?? [];
            // Single center is always displayed regardless of preferences
            if (centers.length <= 1) return centers;
            return centers.filter((center: Center) => center.preferences?.display);
        },

        async getToken(): Promise<Token | null> {
            return this.token ?? await this.getTokenFromStore
        },

        async getTokenFromStore(): Promise<Token | null> {
            let token = await Preferences.get({ key: 'oauth_token' })

            if (token.value) {
                return JSON.parse(token.value)
            }

            return null
        }
    },

    actions: {
        setUser(user: User) {
            // Normalize: ensure every center has a preferences object
            // (the API may return preferences: null for newly-added centers)
            for (const center of user.centers) {
                if (!center.preferences) {
                    (center as any).preferences = {
                        center_id: center.id,
                        label: center.title,
                        display: true,
                        in_dashboard: true,
                    }
                }
            }
            this.user = user
            const appSettingsStore = useAppSettingsStore()
            appSettingsStore.setAccountFetched(true)
            Preferences.set({key: 'centers', value: JSON.stringify(user.centers)})
        },

        async setToken(token: Token | null) {
            log.debug(`[GO-DEBUG][AUTH] setToken: ${token ? 'setting new token' : 'clearing token'}`)
            await this.setTokenInStore(token)
            this.token = token
            if (token !== null) {
                AuthAxios.defaults.headers.common['Authorization'] = `Bearer ${token?.access_token}`

                const expires_at = token.timestamp + token.expires_in * 1000
                const now = new Date().getTime()
                const timeleft = expires_at - now

                if (timeleft < this.refreshTokenLeadTime * 1000) {
                    await this.signinSilent().catch(() => {
                        this.signinRedirect()
                    })
                    return;
                }

                setTimeout(async () => {
                    await this.signinSilent().catch(() => {
                        this.signinRedirect()
                    })
                }, timeleft - this.refreshTokenLeadTime * 1000)
            }
        },

        async setTokenInStore(token: Token | null) {
            if (!token) {
                // Preserve external session signal before clearing — used by signinRedirect
                // to redirect to the session-expired page instead of OAuth
                const externalSession = await Preferences.get({ key: 'external_session' })
                if (externalSession.value === 'true') {
                    sessionStorage.setItem('was_external_session', 'true')
                }
                await Preferences.remove({ key: 'oauth_token' })
                await this.clearExternalSession()
                return
            }

            const jsonedToken = JSON.stringify(token)
            await Preferences.set({ key: 'oauth_token', value: jsonedToken })
        },

        async removeVerifiers() {
            await Preferences.remove({ key: "oauth_state" })
            await Preferences.remove({ key: "oauth_code_verifier" })
        },

        async signinRedirect() {
            log.debug(`[GO-DEBUG][AUTH] signinRedirect called`)
            log.trace(`[GO-DEBUG][AUTH] signinRedirect stack trace`)

            // External session users get a dedicated expired-session page
            // instead of the standard OAuth login redirect
            if (sessionStorage.getItem('was_external_session') === 'true') {
                log.debug(`[GO-DEBUG][AUTH] external session detected → redirecting to session-expired`)
                location.href = import.meta.env.BASE_URL + 'session-expired'
                return
            }

            // Pre-empty codes and states
            await this.removeVerifiers()
            await Preferences.remove({ key: "oauth_state" });
            await Preferences.remove({ key: "oauth_code_verifier" });

            const deviceId = await Device.getId();

            const state = OauthTools.generateRandomState();
            const code_verifier = OauthTools.generateRandomCodeVerifier();
            const code_challenge = OauthTools.calculatePKCECodeChallenge(code_verifier);

            const authorizationUrl = new URL(this.oauth_config.authorization_endpoint);
            authorizationUrl.searchParams.append("client_id", this.oauth_config.client_id);
            authorizationUrl.searchParams.append("response_type", this.oauth_config.response_type);
            authorizationUrl.searchParams.append("redirect_uri", this.oauth_config.redirect_uri);
            authorizationUrl.searchParams.append("state", state);
            authorizationUrl.searchParams.append("code_challenge", code_challenge);
            authorizationUrl.searchParams.append("code_challenge_method", "S256");
            authorizationUrl.searchParams.append("device_id", deviceId.identifier);

            await Preferences.set({ key: "oauth_state", value: state });
            await Preferences.set({ key: "oauth_code_verifier", value: code_verifier });


            location.href = authorizationUrl.toString();
        },

        async signinRedirectCallback(code: string, state: string) {

            const savedState = (await Preferences.get({ key: "oauth_state" })).value;
            const savedCodeVerifier = (await Preferences.get({ key: "oauth_code_verifier" })).value;

            if (state !== savedState) {
                throw new Error("State does not match");
            }

            if (!savedCodeVerifier) {
                throw new Error("Code verifier not found");
            }

            const response = await UserApi.getAccessToken(this.oauth_config.token_endpoint, {
                grant_type: "authorization_code",
                code: code,
                redirect_uri: this.oauth_config.redirect_uri,
                client_id: this.oauth_config.client_id,
                code_verifier: savedCodeVerifier,
            }).catch((error) => {
                return error
            })

            if (response.status !== 200) {
                throw new Error(response)
            }

            const timestamp = new Date().getTime();

            await this.setToken({
                access_token: response.data.access_token,
                refresh_token: response.data.refresh_token,
                expires_in: response.data.expires_in,
                timestamp: timestamp
            });

            await this.removeVerifiers()
            await this.clearExternalSession()

            location.href = import.meta.env.BASE_URL;
        },

        async signinSilent() {
            log.debug(`[GO-DEBUG][AUTH] signinSilent called`)
            const token = await this.getToken
            if (!token) {
                log.debug(`[GO-DEBUG][AUTH] signinSilent: no token, returning`)
                throw new Error('no_token')
            }

            log.debug(`[GO-DEBUG][AUTH] signinSilent: refreshing token...`)
            const response = await UserApi.refreshToken(token.refresh_token).catch((error) => {
                log.debug(`[GO-DEBUG][AUTH] signinSilent: refreshToken error`, error?.message || error)
                this.setToken(null)
                return error
            })

            log.debug(`[GO-DEBUG][AUTH] signinSilent: response status=${response?.status}`)
            if (response.status !== 200) {
                log.debug(`[GO-DEBUG][AUTH] signinSilent: non-200 → throwing to let caller handle redirect`)
                await this.setToken(null)
                throw new Error('token_refresh_failed')
            }

            await this.setToken({ ...response.data, timestamp: new Date().getTime() });
            log.debug(`[GO-DEBUG][AUTH] signinSilent: token refreshed OK`)
        },

        validateAccessToken(token: Token) {
            try {
                this.tokenIsAlive(token)
                return true
            } catch {
                return false
            }
        },

        async updateTokenFromStorage() {
            log.debug(`[GO-DEBUG][AUTH] updateTokenFromStorage called`)
            const token = await this.getTokenFromStore
            log.debug(`[GO-DEBUG][AUTH] updateTokenFromStorage: token from store=${token ? 'exists' : 'null'}`)
            if (!token) {
                return
            }

            await this.setToken(token)

            const externalSession = await Preferences.get({ key: 'external_session' })
            this.isExternalSession = externalSession.value === 'true'
            log.debug(`[GO-DEBUG][AUTH] updateTokenFromStorage: token set, isExternalSession=${this.isExternalSession}`)
        },

        async getUserInfo() {
            log.debug(`[GO-DEBUG][API] getUserInfo called`)
            const response = await UserApi.getUserInfo().catch((error) => {
                log.debug(`[GO-DEBUG][API] getUserInfo network error:`, error?.message || error, `status=${error?.response?.status}`)
                return error
            })

            if (response instanceof AxiosError) {
                log.debug(`[GO-DEBUG][API] getUserInfo AxiosError: status=${response.response?.status}, data=`, response.response?.data)
                if (response.response?.status === 401) {
                    // Token not recognized — start a fresh OAuth cycle
                    // Guard against infinite redirect loops
                    const lastRedirect = Number(sessionStorage.getItem('oauth_redirect_ts') || '0')
                    log.debug(`[GO-DEBUG][API] getUserInfo 401: lastRedirect=${lastRedirect}, now=${Date.now()}, diff=${Date.now() - lastRedirect}ms`)
                    if (Date.now() - lastRedirect < 30_000) {
                        log.debug(`[GO-DEBUG][API] ✗ auth_loop_detected — aborting`)
                        await this.setToken(null)
                        throw new Error('auth_loop_detected')
                    }
                    sessionStorage.setItem('oauth_redirect_ts', String(Date.now()))
                    await this.setToken(null)
                    log.debug(`[GO-DEBUG][API] getUserInfo 401 → signinRedirect`)
                    this.signinRedirect()
                    return
                }

                log.debug(`[GO-DEBUG][API] getUserInfo non-401 error → throwing`)
                throw new Error(response.response?.data)
            }

            log.debug(`[GO-DEBUG][API] getUserInfo response status=${response.status}`)
            if (response.status !== 200) {
                log.debug(`[GO-DEBUG][API] getUserInfo non-200 status=${response.status} → throwing`)
                throw new Error(response.response.data)
            }

            log.debug(`[GO-DEBUG][API] getUserInfo OK: centers=${response.data.centers?.length}, components=${response.data.components?.length}`)
            const appSettingsStore = useAppSettingsStore()
            appSettingsStore.setCenters(response.data.centers)
            this.setUser(response.data)
            log.debug(`[GO-DEBUG][API] getUserInfo: user set in store`)
        },

        tokenIsAlive(token: Token) {
            const now = new Date().getTime();
            const expires_at = token.timestamp + token.expires_in * 1000;

            if (now > expires_at) {
                throw new Error("Token is expired")
            }
        },

        /**
         * Sends a request to connect to a GoKarts server
         */
        async connectToCenter(login: string, password: string) {
            const resp = await AuthGokartsApi.connectToCenter(login, password).catch((error) => {
                return error
            })

            if (resp.status !== 204) {
                const errorMsg = resp.response.data?.error?.message ?? ""
                const errorKey = errorMsg.split(": ").pop() || "invalid_credentials"
                throw new Error(errorKey)
            }

            // Redirect user to GoKarts user login
            return resp
        },

        /**
         * Fetches the users of the previously connected center via connectToCenter
         */
        async getConnectingCenter() {
            const resp = await AuthGokartsApi.getAuthenticatingCenter().catch((error) => {
                return error
            })

            if (resp.status !== 200) {
                throw new Error("Error fetching users")
            }

            return resp.data
        },

        /**
         * Connects the user to a GoKarts user
         */
        async connectToCenterUser(login: string, password: string) {
            const resp = await AuthGokartsApi.connectToCenterUser(login, password).catch((error) => {
                return error
            })

            if (resp.status !== 204) {
                const status = resp.response?.status ?? resp.status;
                if (status === 401) {
                    return new Error('invalid_password');
                }
                return new Error(resp.response?.data?.error?.message ?? resp.response?.data?.message ?? 'unknown_error');
            }

            return resp
        },

        async setExternalSession(value: boolean) {
            this.isExternalSession = value
            await Preferences.set({ key: 'external_session', value: String(value) })
        },

        async clearExternalSession() {
            this.isExternalSession = false
            await Preferences.remove({ key: 'external_session' })
        },

        removeCenter(id: number) {
            const index = this.user?.centers.findIndex((center) => center.id === id);
            if (index != undefined && index !== -1) {
                this.user?.centers.splice(index, 1);
            }
        },

        removeComponent(id: string) {
            const index = this.user?.components.findIndex((component) => component.id === id);
            if (index != undefined && index !== -1) {
                this.user?.components.splice(index, 1);
            }
        }
    }
})